import { escapeHtml, discountPercent } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent } from '../types.js';
import {
  ARABIC_LINE,
  badge,
  description,
  lineCount,
  price,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';
import {
  CC_FAMILY,
  CC_THEME,
  CC_VARIANTS,
  ccDeco,
  ccDecoCss,
  ccHeadHeight,
  ccHeader,
  ccHeaderCss,
  ccHeaderHeight,
  ccHeading,
  ccPriceCircle,
  ccPriceCircleCss,
  ccRing,
  ccRingCss,
  ccRootCss,
  ccRowsCss,
} from './chalk-chili-trio.js';

/**
 * Chalk & Chili, layout C: every dish as a card, a round photo in a red ring with its price
 * circle on the rim and its name and description under it, in rows under the script section
 * headings. For pizzas, sides or desserts where the photos sell.
 */

const S = '.mb-t-chalk-chili-rings';
const TITLE_EM = 4.4;
const PER_ROW = { landscape: 5, portrait: 3 };

function cardHeight(board: MenuBoardContent, item: MenuItemContent, perLine: number): number {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  const second = en && ar && item.titleAr?.trim() ? 1.15 * 1.3 * ARABIC_LINE : 0;
  const extra = item.variants.length || discountPercent(item) !== null ? 1.05 * 1.45 : 0;
  const lines = board.settings.showDescriptions
    ? (en || !item.descriptionAr ? lineCount(item.description, perLine, 2) : 0) +
      (ar ? lineCount(item.descriptionAr, perLine, 2) * ARABIC_LINE : 0)
    : 0;
  return 11.8 + 1.5 * 1.2 + second + extra + (lines ? 0.25 + lines * 0.95 * 1.3 : 0) + 1.8;
}

function column(board: MenuBoardContent, portrait: boolean): MenuColumn {
  const perRow = portrait ? PER_ROW.portrait : PER_ROW.landscape;
  const header = ccHeaderHeight(board, TITLE_EM) + 1.6;
  return {
    capacity: (portrait ? 120 : 67.5) - 5 - header,
    blockCost: (section, items, continued) => {
      let rows = 0;
      for (let i = 0; i < items.length; i += perRow) {
        rows += Math.max(...items.slice(i, i + perRow).map((it) => cardHeight(board, it, 36)));
      }
      return ccHeadHeight(board, section, continued) + rows + 0.4;
    },
  };
}

function card(ctx: RenderContext, item: MenuItemContent): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<p class="cc3-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</p>`
    : `<p class="cc3-name">${escapeHtml(item.title)}</p>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="cc-name2 cc3-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  const extra = item.variants.length
    ? `<p class="cc-sizes cc3-sizes">${item.variants
        .map((v) => `<span class="cc-size"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <b dir="ltr">${price(ctx, v.price)}</b></span>`)
        .join('')}</p>`
    : discountPercent(item) !== null
      ? `<p class="cc-sizes cc3-sizes"><s class="cc-old" dir="ltr">${price(ctx, item.price)}</s></p>`
      : '';
  const badges = badge(ctx, item, 'cc-badge');
  return [
    '<div class="cc3-card">',
    '<div class="cc3-stage">',
    ccRing(ctx, item.imageMediaId, 'cc3-ring'),
    item.variants.length ? '' : ccPriceCircle(ctx, item.discountPrice ?? item.price),
    badges ? `<span class="cc3-badges">${badges}</span>` : '',
    '</div>',
    name,
    second,
    extra,
    description(ctx, item, 'cc-desc'),
    '</div>',
  ].join('');
}

function section(ctx: RenderContext, b: MenuBlock): string {
  return `<div class="cc3-sec">${ccHeading(ctx, b)}<div class="cc3-cards">${b.items.map((i) => card(ctx, i)).join('')}</div></div>`;
}

export const chalkChiliRings: MenuTemplate = {
  id: 'chalk-chili-rings',
  name: 'Chalk & Chili · Photo rings',
  description:
    'Every dish as a round photo in a red ring with its price circle on the rim, name and description underneath, in rows under script headings on a chalkboard. Matches the other Chalk & Chili screens.',
  defaultTheme: CC_THEME,
  variants: CC_VARIANTS,
  cuisines: ['pizza', 'pasta', 'cafe'],
  family: { ...CC_FAMILY, layout: 'Photo rings' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), [column(board, orientation === 'portrait')]);
  },

  render(ctx) {
    const body = ctx.page.columns.flat().map((b) => section(ctx, b)).join('');
    return [
      '<div class="cc3-top">',
      ccDeco('leaf', 'cc3-d1'),
      ccDeco('tomato', 'cc3-d2'),
      ccHeader(ctx, 'cc3-header', ctx.orientation === 'portrait' ? 14 : 22),
      ccDeco('chili', 'cc3-d3'),
      ccDeco('leaf', 'cc3-d4'),
      '</div>',
      `<div class="cc3-body">${body}</div>`,
    ].join('');
  },

  css: `
${ccRootCss(S)}
${S}{padding:2.5em 3em}
${ccDecoCss(S)}
${ccRingCss(S)}
${ccRowsCss(S)}
${ccHeaderCss(S, 'cc3-header', TITLE_EM)}
${ccPriceCircleCss(S)}
${S} .cc3-top{position:relative;display:flex;justify-content:center;margin-bottom:1.6em}
${S} .cc3-header{text-align:center;max-width:70%}
${S}.mb-portrait .cc3-header{max-width:80%}
${S} .cc3-d1{left:4%;top:.5em;transform:rotate(-25deg)}
${S} .cc3-d2{left:11%;top:4em}
${S} .cc3-d3{right:11%;top:.8em}
${S} .cc3-d4{right:4%;top:4.2em;transform:scaleX(-1) rotate(-20deg)}
${S}.mb-portrait .cc3-d2{left:2%;top:5.5em}
${S}.mb-portrait .cc3-d3{right:2%;top:5.5em}
${S}.mb-portrait .cc3-d1{left:3%;top:0}
${S}.mb-portrait .cc3-d4{right:3%;top:0}
${S} .cc3-body{flex:1;min-height:0;display:flex;flex-direction:column}
${S} .cc3-sec{margin-bottom:.4em}
${S} .cc3-cards{display:flex;flex-wrap:wrap;justify-content:center;column-gap:2.5em}
${S} .cc3-card{flex:0 0 calc((100% - 10em) / 5);min-width:0;display:flex;flex-direction:column;align-items:center;text-align:center;margin-bottom:1.8em}
${S}.mb-portrait .cc3-card{flex-basis:calc((100% - 5em) / 3)}
${S} .cc3-stage{position:relative;width:11em;height:11em;margin-bottom:.8em}
${S} .cc3-ring{width:100%;height:100%}
${S} .cc3-stage .cc-pc{position:absolute;z-index:2;right:-1.4em;bottom:-.2em}
${S} .cc3-badges{position:absolute;z-index:2;left:-1em;top:.2em;display:flex;flex-direction:column;align-items:flex-start;gap:.3em}
${S} .cc3-name{max-width:100%;font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.5em;line-height:1.2;letter-spacing:.05em;
  text-transform:uppercase;color:var(--mb-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .cc3-name.mb-ar{letter-spacing:0}
${S} .cc3-name2{max-width:100%}
${S} .cc3-sizes{justify-content:center}
`,
};
